import { Effect } from '@babylonjs/core/Materials/effect';
import { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { createEffect } from 'solid-js';
import { Show, createSignal } from 'solid-js';
import { render } from 'solid-js/web';

import { autoEnter } from './game/compat';
import { BoltField } from './game/bolts';
import { renderFeatures } from './game/compat';
import { attachAudio } from './audio/attach';
import { createAudioContext, createMixer, type Mixer } from './audio/mixer';
import { SampleBank } from './audio/samples';
import { createMusicPlayer, MusicDirector, type MusicSink } from './audio/music';
import { createScene, CAMERA_FOLLOW_RATE } from './game/engine';
import { gradeUsable } from './game/grading';
import { Gate } from './ui/Gate';
import { TitleScreen } from './ui/TitleScreen';
import { canStepAmong } from './game/zone/fixtures';
import { ZoneRenderer } from './game/zone/render';
import { TorchLights } from './game/zone/torchlight';
import { EntityMeshes } from './game/entities';
import { ProceduralModelFactory } from './game/models/procedural';
import { PropLibrary } from './game/models/props';
import { attachDiagnostics } from './game/diagnostics';
import { attachInput, GATE_ARRIVAL_MARGIN } from './game/input';
import { Navigator } from './game/navigate';
import { LootMeshes } from './game/loot';
import { attachKeyboard } from './game/keyboard';
import { smoothingAlpha } from './game/lerp';
import { ScreenProjector } from './game/project';
import { SnapshotBuffer } from './net/interpolate';
import { PlayerPredictor } from './net/predict';
import { GameSocket } from './net/socket';
import { createGameStore } from './state/store';
import { useGate } from './net/protocol';
import { App } from './ui/App';

import './styles/index.css';

/**
 * Bootstraps the client: store, scene, HUD, then — only once the player leaves
 * the title screen — the socket.
 *
 * The wiring order is the whole decoupling story. The socket writes into the
 * store; the HUD reads it reactively; the render loop reads its non-reactive
 * mirror. Babylon and Solid never reference each other.
 */
/**
 * Boots the gate, and only then the game.
 *
 * **The order is the point.** `main` builds a Babylon engine, compiles shaders and starts a
 * render loop; none of that happens until a character has been chosen, which is the last
 * part of the "no 3D allocation for an unauthenticated client" posture in
 * `CLI-USER-MANAGEMENT-SECURE-LOGIN.md`. On a server with accounts switched off the gate
 * passes straight through and this is one extra microtask.
 */
function boot(): void {
  const hudRoot = document.getElementById('hud-root');
  if (!hudRoot) throw new Error('grind: index.html is missing #hud-root');

  // **The title screen comes first**, then signing in and picking a character.
  //
  // It used to sit *inside* the game — a scrim over the Babylon scene, shown until the
  // player pressed Enter — so the order was login, roster, title, play. Asked for directly:
  // *we need to start with the title screen then after select/create character.* Which is
  // also the right way round: the title is the game introducing itself, and a login form is
  // a thing you do once you have decided to play.
  //
  // Neither of these builds the engine. See `Gate` for why that matters.
  const [titled, setTitled] = createSignal(false);

  const disposeGate = render(
    () => (
      <Show when={titled()} fallback={<TitleScreen onEnter={() => setTitled(true)} />}>
        <Gate
          onPlay={(characterID) => {
            // The gate's DOM goes before the scene is built, so the two never coexist —
            // a canvas rendering behind a login form is a canvas nobody asked for.
            disposeGate();
            main(characterID);
          }}
        />
      </Show>
    ),
    hudRoot,
  );
}

function main(characterID?: string): void {
  const canvas = document.getElementById('renderCanvas');
  const hudRoot = document.getElementById('hud-root');
  if (!(canvas instanceof HTMLCanvasElement) || !hudRoot) {
    throw new Error('grind: index.html is missing #renderCanvas or #hud-root');
  }

  // When a shader fails to compile, Babylon logs the driver's message but not
  // the source it was compiling, so a bare "VERTEX SHADER ERROR: 0:52" is
  // unactionable. In development, dump the generated GLSL with it — that is what
  // identified the uniform-buffer incompatibility documented in game/engine.ts.
  if (import.meta.env.DEV) Effect.LogShaderCodeOnCompilationError = true;

  const store = createGameStore();

  // Snapshots are buffered and replayed slightly in the past so entity motion is
  // interpolated between two known states rather than chased. The local player
  // bypasses that entirely and is predicted at full frame rate.
  const buffer = new SnapshotBuffer();
  const predictor = new PlayerPredictor((to, from) => {
    const zone = store.zone();
    // The server's rule, body clearance and standing doors and barrels included (`canStepAmong`,
    // `walkableAt` in movement.go).
    return zone ? canStepAmong(zone, from, to, store.fixturesDown()) : true;
  });

  // The scene boots immediately and renders behind the title screen, so
  // entering the world is a fade rather than a load. It draws an empty, lit
  // ground plane until the first snapshot arrives.
  const { engine, scene, camera, shadows, ambient, sun, pixelArt, colourGrade, glow, dispose } = createScene(canvas);

  // The floor is the zone's, built when the ZONE frame arrives. Before that the
  // scene is empty black — which is what the title screen sits over.
  // Real light from the torches nearest the camera (`zone/torchlight.ts`).
  const torches = renderFeatures.torchlight ? new TorchLights(scene, () => camera.target) : null;
  const zoneRenderer = new ZoneRenderer(scene, { ambient, sun, shadows, glow, torches });
  // **The generated props (Phase 42), loaded the way the bodies are: fire and forget.** A zone drawn
  // before they land is drawn procedurally, and `refresh` redraws it once they have — a slow fetch
  // costs a moment of boxes, never a loading screen.
  const props = new PropLibrary(scene);
  zoneRenderer.useProps(props);
  const propsLoaded = props.preload();
  void propsLoaded.then(() => zoneRenderer.refresh());

  // Projectiles. Decoration over an outcome the server already resolved, so safe
  // mode does without them and nothing about the game changes.
  const bolts = renderFeatures.textures ? new BoltField(scene, glow) : undefined;
  // **The model factory is built here so its bodies can be preloaded.** `EntityMeshes` would
  // otherwise make its own, and the rigged `.glb` bodies have to be fetched before the first
  // character is drawn — see `ProceduralModelFactory.preload` for why nothing awaits this: an
  // entity arriving inside that window gets the procedural box rig, which is the fallback the
  // whole design keeps intact rather than a failure state.
  const models = new ProceduralModelFactory(scene);
  models.useProps(props);
  const meshes = new EntityMeshes(scene, shadows, store, buffer, predictor, models, bolts);
  // Everyone already drawn is redrawn once the bodies and their gear are in (`rebuildAll`).
  void Promise.all([models.preload(), propsLoaded]).then(() => meshes.rebuildAll());
  const loot = new LootMeshes(scene, store);
  const projector = new ScreenProjector(scene, camera, store);
  // Routes clicks round walls, and owns every walk order and every stop.
  const navigator = new Navigator(predictor, store);
  const detachInput = attachInput(
    scene,
    () => zoneRenderer.groundMeshes,
    meshes,
    loot,
    navigator,
    // The town's portals and artisans. `ZoneRenderer` owns their colliders because it
    // owns the geometry they are measured from; `attachInput` only ever asks it the two
    // questions a pointer has.
    zoneRenderer,
    store,
  );
  // The navigator is handed over so the spacebar can roll: a dodge is movement, and
  // predicting it and sending it must happen in one place. Everything else the keyboard
  // does goes through the store alone.
  const detachKeyboard = attachKeyboard(store, document, navigator);

  // Leaving for the title screen closes the socket. Registered as a sink so the
  // menu that triggers it does not need the socket threaded down to it.
  const socket = new GameSocket({
    // Which character this connection is for. Undefined on a server with no accounts, which
    // connects anonymously exactly as the game did before they existed.
    characterID,
    onFrame: (frame) => {
      store.applyFrame(frame);

      // Buffer snapshots for interpolation. Done here rather than in the store so
      // the render loop can read it without touching the reactive graph.
      //
      // **The assembled world, not the wire's delta** (2026-09-29). The wire omits an entity whose
      // visible values did not change, so `frame.payload.entities` holds only what moved — and
      // `buffer.latest(selfId)` found nothing for a player standing still. After a zone change the
      // sync below waited on a position that never came, the predictor kept its first (0, 0), and
      // the player was drawn at the world origin while the server had them at the entry: inside a
      // void block, as often as not, with the room around them black — reported as characters
      // *lost in the wall*. `store.entities()` is `SnapshotAssembler`'s complete list for the frame.
      if (frame.type === 'SNAPSHOT') {
        buffer.push(frame.payload.tick, store.entities(), performance.now());
      }
      // A zone change invalidates every buffered position, and the player has
      // been teleported — both must be reset or the first frame interpolates
      // across the whole map.
      if (frame.type === 'ZONE') {
        buffer.clear();
        /*
          **Adopt the *next* authoritative position, rather than reading the last one.**

          This used to do `predictor.reset(store.raw.get(selfId))` — and `raw` still holds the
          position from the zone just **left**, because a ZONE frame arrives before the first snapshot
          of the new one and nothing clears positions on it (`world.clear()` empties the description
          cache, not `raw`; only `returnToTitle` clears that). So the reset pinned the predictor to the
          old zone's coordinates, which in town are **outside the walls**. Reported exactly that way:
          *using the teleport back to town places us out of bounds, and sending anything to the server
          moves us to the right place.*

          There is no newer position to read either: at the moment this frame lands, nothing on the
          client knows where the player is. The only correct answer is to wait one frame and take what
          the server says.

          Taken **unconditionally** when it arrives — `reset`, not `reconcile`. A zone change is a
          teleport the client could not have predicted, so there is no local intent worth preserving
          and nothing to ease toward; and it must not depend on `acknowledged`, which is what left the
          old position standing until the player happened to send another command.
        */
        awaitingZoneSync = true;
        // The route was a list of tiles in the zone that has just been left.
        // Walking them here walks into walls.
        navigator.clear();
      }
    },
    onStatus: (connected) => store.setConnected(connected),
    // Terminal: the socket will not dial back, so the HUD has to say so rather than
    // showing a reconnect that is never coming.
    onDisplaced: () => store.setDisplaced(true),
  });
  store.onCommand((cmd) => socket.send(cmd));
  store.onExit(() => {
    socket.close();
    // Every sound mid-envelope would otherwise keep ringing over a title screen
    // with no world behind it.
    audio?.stopAll();
    bolts?.clear();
    // The interpolation window belongs to the session that just ended; keeping it
    // would have entities gliding in from the old world on re-entry. The meshes
    // themselves are disposed by the render loop, which the store tells about
    // every id at once — see returnToTitle.
    buffer.clear();
    navigator.clear();
  });

  /**
   * True between a ZONE frame and the first snapshot of the new zone.
   *
   * The window in which **nothing on the client knows where the player is**: the old position has
   * been cleared and the new one has not arrived. See the ZONE handler for why this cannot be
   * resolved at the moment the frame lands.
   */
  let awaitingZoneSync = false;

  // Reused every frame — nothing in this loop may allocate.
  const cameraTarget = new Vector3(0, 0, 0);

  scene.onBeforeRenderObservable.add(() => {
    // One clock reading for the whole frame: sampling it more than once would let
    // interpolation and prediction disagree about when "now" is.
    const now = performance.now();
    const dt = engine.getDeltaTime() / 1000;

    // Rebuilding is a no-op unless the seed or biome actually changed, so this
    // is cheap to check every frame and means a re-sent ZONE frame just works.
    const zone = store.zone();
    if (zone) zoneRenderer.build(zone);
    // A town portal is the one piece of zone geometry that appears mid-zone, so it
    // cannot ride the seed-keyed rebuild above. Idempotent in the same way: the ids
    // are compared and nothing happens unless a portal opened or closed.
    zoneRenderer.syncPortals(store.portals());
    // Doors swing and barrels break when the snapshot's `down` says so (`LEVEL-FIXTURES.md`).
    zoneRenderer.syncFixtures(store.fixturesDown(), dt);

    // Predict the local player before anything reads its position.
    const selfId = store.selfId();
    const authoritative = selfId === null ? undefined : buffer.latest(selfId);

    // The first authoritative position after a zone change is adopted outright — see the ZONE
    // handler above. Before `step`, so the frame this lands on renders from the new position rather
    // than predicting one more step from the old one.
    if (awaitingZoneSync && authoritative) {
      predictor.reset({ x: authoritative.x, z: authoritative.z });
      awaitingZoneSync = false;
    }

    predictor.step({
      dt,
      speed: store.playerSpeed(),
      // The ramp's shape is the server's, exactly as the speed is. Both halves
      // integrate the same curve or the opening frames of every walk are a correction.
      rampTime: store.walkRampTime(),
      server: authoritative ? { x: authoritative.x, z: authoritative.z } : undefined,
      // Until the server has seen our latest order its position reflects an older
      // one, and reconciling would drag us back along a path we have abandoned.
      acknowledged: (authoritative?.ackSeq ?? 0) >= predictor.latestSeq,
      frozen: store.dead(),
      // Crowd control, straight off the authoritative snapshot rather than through the store.
      // Phase 32.4: a hold or a slow the predictor does not know about is drift, not a missing
      // effect — see `held` in net/protocol.ts. Absent means free and ×1, which is the usual case.
      held: authoritative?.held ?? false,
      moveScale: authoritative?.moveScale ?? 1,
    });

    // Then hand the route the position prediction just produced, so arriving at a
    // corner sends the next leg on the same frame rather than the next one.
    //
    // A dead player has a frozen prediction, so a route they died partway through
    // would sit on its current leg and then resume it on respawn — from wherever
    // they respawned, which is somewhere else entirely.
    if (store.dead()) navigator.clear();
    else navigator.tick(now);

    meshes.update(dt, now);
    bolts?.update(dt);
    loot.update(dt);

    // The camera follows the *predicted* position, so it moves on the click frame
    // rather than a tick later.
    const predicted = predictor.current;
    cameraTarget.set(predicted.x, 0, predicted.z);
    Vector3.LerpToRef(camera.target, cameraTarget, smoothingAlpha(dt, CAMERA_FOLLOW_RATE), camera.target);

    projector.update(predicted);
    store.expireDamage();
  });

  // Dev-only on-screen readout: babylon version, GPU, feature flags, mesh and
  // effect counts. A screenshot of a broken scene is then a full diagnosis.
  const detachDiagnostics = import.meta.env.DEV ? attachDiagnostics(engine, scene) : () => {};

  // ── Settings → the scene ──────────────────────────────────────────────────
  //
  // The one place a Solid signal reaches a Babylon object. Everything else keeps
  // the two apart; this is the seam, and it runs one way only — the store never
  // learns anything back except whether a feature exists at all.
  if (!pixelArt) {
    // `?pixelate=0`, or the shader failed to compile and disposed itself. Either
    // way the toggle would be a lie, so the menu greys it.
    store.markSettingUnavailable('pixelate');
  }
  if (!colourGrade) store.markSettingUnavailable('grading');

  /**
   * The mixer and the music, built on the **first user gesture of any kind**.
   *
   * A browser refuses to start an `AudioContext` outside a gesture, and one
   * created too early lands `suspended` and stays there — the game would be
   * silent with nothing in the console to say why.
   *
   * ENTER used to be the trigger, which was enough while everything was a
   * response to something the player did in the world. It is not enough for the
   * title theme, which should be playing *on* the title screen — so any gesture
   * starts audio, and ENTER is simply the last one that can. Nothing can make
   * sound before the player has touched the page; no browser allows otherwise,
   * and there is no way to arrange one.
   */
  let audio: Mixer | null = null;
  let music: MusicSink | null = null;
  let director: MusicDirector | null = null;
  let detachAudio: (() => void) | null = null;

  const startAudio = () => {
    if (audio) return;
    const context = createAudioContext();
    // No WebAudio at all — an old browser, or a locked-down one. The game is
    // playable without it, so this is a missing feature rather than an error.
    if (!context) return;

    // The recorded sound effects, decoded in the background: every cue played before they land
    // is its synthesised sound, so nothing waits on this and nothing is quieter for it.
    const samples = new SampleBank();
    void samples.load(context);
    audio = createMixer(context, store.settings().volume, samples);
    detachAudio = attachAudio(store, audio);

    music = createMusicPlayer(
      context,
      audio.master,
      store.settings().musicVolume,
      import.meta.env.BASE_URL,
    );
    director = new MusicDirector(music);
    // Catch up immediately: the gesture that started audio may well have been the
    // ENTER click, in which case the phase has already changed and the effect
    // below has already run for the last time.
    director.update(store.phase(), store.zone()?.kind ?? null);
  };

  // One-shot, capture-phase, on both a pointer and a key so a keyboard-only
  // player is not left in silence.
  const firstGesture = () => {
    startAudio();
    window.removeEventListener('pointerdown', firstGesture, true);
    window.removeEventListener('keydown', firstGesture, true);
  };
  window.addEventListener('pointerdown', firstGesture, true);
  window.addEventListener('keydown', firstGesture, true);

  // `?enter=1`: skip the title for a capture tool, which cannot click. Audio is
  // deliberately not started — there has been no gesture, and no browser would
  // allow it anyway.
  if (autoEnter()) {
    store.enterGame();
    socket.connect();
  }

  // Volume gets an effect of its own rather than riding along with the render
  // settings. Solid re-runs an effect whenever *any* signal it read changes, and
  // the render effect attaches the pixel-art post-process — so folding volume in
  // would re-attach it on every step of the slider, which Babylon answers with
  // "you're trying to reuse a post process not defined as reusable".
  // **Read the signals before the optional call, always.**
  //
  // `audio?.setVolume(store.settings().volume)` looks equivalent and is not:
  // optional chaining short-circuits, so on the first run — when `audio` is still
  // null, because the mixer waits for a gesture — the signal is never read, the
  // effect records no dependency, and it never runs again. The volume slider then
  // does nothing for the rest of the session, silently. Same trap for the music
  // track, which is how it was found: the title theme started and the game never
  // switched to the town or dungeon themes.
  createEffect(() => {
    const volume = store.settings().volume;
    audio?.setVolume(volume);
  });
  createEffect(() => {
    const musicVolume = store.settings().musicVolume;
    music?.setVolume(musicVolume);
  });

  // The track follows the two facts that say where the player is. Both are
  // signals, so a zone transition changes the music without anything having to
  // remember to; MusicDirector is what stops that re-triggering the same track.
  createEffect(() => {
    const phase = store.phase();
    const zoneKind = store.zone()?.kind ?? null;
    director?.update(phase, zoneKind);
  });

  // `attachPixelArt` builds it detached, so the effect below attaches it only if the setting is on.
  let pixelArtAttached = false;
  let gradeAttached = colourGrade !== null;

  createEffect(() => {
    const { pixelate, grading, fog, shadows: castShadows } = store.settings();

    if (pixelArt && pixelate !== pixelArtAttached) {
      // A post-process has no enabled flag; attaching and detaching is how it is
      // switched. Attaching one twice is *not* harmless — Babylon logs "you're
      // trying to reuse a post process not defined as reusable" — and this effect
      // re-runs on any settings change, volume included, because `settings` is
      // one signal holding one object. So the current state is tracked.
      // **At the front of the chain**, so the grade stays after it: re-attached at the end, the
      // pixel art would quantise the vignette's gradient into rings.
      if (pixelate) camera.attachPostProcess(pixelArt, 0);
      else camera.detachPostProcess(pixelArt);
      pixelArtAttached = pixelate;
    }
    // A shader failure disposes the grade; a disposed post-process must not be re-attached.
    if (colourGrade && grading !== gradeAttached && gradeUsable(colourGrade)) {
      if (grading) camera.attachPostProcess(colourGrade);
      else camera.detachPostProcess(colourGrade);
      gradeAttached = grading;
    }

    scene.fogMode = fog ? Scene.FOGMODE_EXP2 : Scene.FOGMODE_NONE;
    sun.shadowEnabled = castShadows;
  });

  // **Entered here rather than behind a second button.**
  //
  // `main` is called from the character selector's own click handler, so this runs inside a
  // real user gesture — which is what `startAudio` needs to resume an AudioContext. Moving
  // it any later would mean a silent game until the player happened to click something.
  store.enterGame();
  socket.connect();
  startAudio();

  engine.runRenderLoop(() => scene.render());

  const disposeUI = render(
    () => (
      <App
        store={store}
        // Kept so `App` can still ask, but nothing reaches it: the title screen is before
        // the gate now, and entering happens below the moment a character is chosen.
        onEnter={() => {}}
        // The HUD's "Back to the Run" button. Walking is the Navigator's, so the HUD asks
        // and this steers — the same walk-then-use a click on the gate performs, and the
        // same one `T` in town does.
        onWalkToGate={(gate) => {
          navigator.goTo(gate.x, gate.z, {
            at: { x: gate.x, z: gate.z },
            radius: Math.max(1, store.portalEntryRange() - GATE_ARRIVAL_MARGIN),
            done: () => store.send(useGate('PORTAL')),
          });
        }}
      />
    ),
    hudRoot,
  );

  window.addEventListener('beforeunload', () => {
    detachAudio?.();
    music?.dispose();
    audio?.dispose();
    detachDiagnostics();
    detachKeyboard();
    detachInput();
    disposeUI();
    zoneRenderer.dispose();
    loot.dispose();
    meshes.dispose();
    socket.close();
    dispose();
  });
}

boot();
